export interface TripBackupReminderData {
  accountId: string;
  lastDismissedAt?: string;
  lastDownloadedAt?: string;
}

export class TripBackupReminderCache {
  private static CACHE_KEY = "trip_backup_reminder";
  private static QUARTERLY_PERIOD_IN_MS = 90 * 24 * 60 * 60 * 1000; // 90 days (quarterly) in milliseconds

  /**
   * Save the last action taken by the user (dismiss or download)
   */
  static saveAction(accountId: string, action: "dismiss" | "download"): void {
    const now = new Date().toISOString();
    const cached = this.getReminderData(accountId);

    const updatedData: TripBackupReminderData = {
      accountId,
      lastDismissedAt: cached?.lastDismissedAt,
      lastDownloadedAt: cached?.lastDownloadedAt
    };

    if (action === "dismiss") {
      updatedData.lastDismissedAt = now;
    } else if (action === "download") {
      updatedData.lastDownloadedAt = now;
    }

    localStorage.setItem(this.CACHE_KEY, JSON.stringify(updatedData));
  }

  /**
   * Get reminder data for a specific account
   */
  static getReminderData(accountId: string): TripBackupReminderData | null {
    const cached = localStorage.getItem(this.CACHE_KEY);
    if (!cached) return null;

    try {
      const data: TripBackupReminderData = JSON.parse(cached);
      // Return data only if it matches the current account
      return data.accountId === accountId ? data : null;
    } catch {
      return null;
    }
  }

  /**
   * Check if the reminder should be shown (quarterly - 90 days have passed since last action)
   */
  static shouldShowReminder(accountId: string): boolean {
    const cached = this.getReminderData(accountId);

    if (!cached) {
      // Never shown before, should show
      return true;
    }

    // Get the most recent action date
    const lastActionDate = this.getLastActionDate(cached);

    if (!lastActionDate) {
      // No action recorded yet, should show
      return true;
    }

    const now = new Date().getTime();
    const lastAction = new Date(lastActionDate).getTime();
    const timeDiff = now - lastAction;

    // Show if quarterly period (90 days) has passed
    return timeDiff >= this.QUARTERLY_PERIOD_IN_MS;
  }

  /**
   * Get the most recent action date from cached data
   */
  private static getLastActionDate(
    data: TripBackupReminderData
  ): string | null {
    const { lastDismissedAt, lastDownloadedAt } = data;

    if (!lastDismissedAt && !lastDownloadedAt) return null;
    if (!lastDismissedAt) return lastDownloadedAt!;
    if (!lastDownloadedAt) return lastDismissedAt;

    // Return the most recent date
    return new Date(lastDismissedAt) > new Date(lastDownloadedAt)
      ? lastDismissedAt
      : lastDownloadedAt;
  }

  /**
   * Clear cache for a specific account or all
   */
  static clearCache(accountId?: string): void {
    if (!accountId) {
      localStorage.removeItem(this.CACHE_KEY);
      return;
    }

    const cached = this.getReminderData(accountId);
    if (cached && cached.accountId === accountId) {
      localStorage.removeItem(this.CACHE_KEY);
    }
  }
}
